import React, { useEffect, useRef, useState } from 'react';
import { Price } from '@services';
import { CreatePaymentWidgetParams, MountableElement, PAYMENT_METHODS } from 'integrations/payment';

interface PayPalProps {
  amount?: Price;
  placeOrder?: (resultCode: string, refusalReason?: string) => Promise<boolean>;
  onNextStep?: () => void
  onError?: (errorMessage: string) => void;
  createPaymentWidget: (params: CreatePaymentWidgetParams) => Promise<MountableElement>;
  isSubscription?: boolean;
}

export const PayPal: React.FC<PayPalProps> = ({
  amount,
  placeOrder,
  onNextStep,
  onError,
  createPaymentWidget,
  isSubscription
}) => {
  const paypalPaymentContainer = useRef<HTMLDivElement>(null);
  const [ remountKey, setRemountKey ] = useState(0);

  const createCheckout = async (isSubscription?: boolean): Promise<void> => {
    if (paypalPaymentContainer.current) {
      paypalPaymentContainer.current.innerHTML = '';
    }

    const widget = await createPaymentWidget({
      paymentMethodType: PAYMENT_METHODS.PayPal,
      onSubmit: async (resultCode: string, refusalReason?: string): Promise<void> => {
        switch (resultCode) {
        case 'Refused':
          onError?.(`Payment is refused - ${refusalReason}`);
          setRemountKey(prev => prev + 1);

          return;

        case 'Pending':
          return;

        case 'Authorised': {
          const order = await placeOrder?.(resultCode, refusalReason);
          if (order) {
            onNextStep?.();
          }

          return;
        }

        default:
          return;
        }
      },
      amount: {
        value: amount?.value ?? 0,
        currency: amount?.currencyCode ?? 'USD'
      },
      isSubscription
    });

    if (paypalPaymentContainer.current) {
      widget.mount(paypalPaymentContainer.current);
    }
  };

  useEffect(() => {
    createCheckout(isSubscription);

    return (): void => {
      if (paypalPaymentContainer.current) {
        paypalPaymentContainer.current.innerHTML = '';
      }
    };
  }, [ isSubscription, remountKey ]);

  return <div ref={ paypalPaymentContainer } key={ remountKey }></div>;
};
